// @ts-nocheck — profile page, types added in a later sub-step
import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  getUser, updateUser, listMarketCatalog, canonicalUserSlug,
  getSharedState, getActuals,
} from "../lib/data";
import type { User, MarketCatalogEntry } from "../lib/data";
import { useViewer } from "../lib/data/viewer";
import { canWriteUserDisplayFields, canWriteUserMetadata } from "../lib/data/rbac";
import { isMarketSupported } from "../lib/markets-config";
// Phase 3c — adopt Form/Field/Input/Button primitives for the edit form.
import Form from "../components/ui/Form";
import V2Field from "../components/ui/Field";
import Input from "../components/ui/Input";
import V2Button from "../components/ui/Button";
// Phase 3d — direction icons pair with attainment % color so the signal
// isn't color-only.
import { ArrowUp, ArrowDown, Minus } from "../icons";
// Phase 3e — section-level empty/error/loading states use primitives.
import EmptyState from "../components/ui/EmptyState";
import ErrorState from "../components/ui/ErrorState";
import Skeleton from "../components/ui/Skeleton";
// Phase 4b audit — DR-23 (DataTable) compliance.
import DataTable from "../components/ui/DataTable";
// Phase 4b audit — DR-14: wrap title= cells in v2 Tooltip.
import Tooltip from "../components/ui/Tooltip";
// Phase 4e — IT/EN parity. UserProfile was English-only; reads the global
// lang preference set by ForecastItaly's lang toggle.
import { useLang } from "../lib/lang";
// Hotfix (2026-05-16) — shared validated worker URL resolver.
import { readWorkerUrl } from "../lib/worker-url";
// Phase B1 — layout primitive.
import Page from "../components/ui/Page";

/* src/views/UserProfile.tsx — /u/:userId (Phase 6).

   Read view is open per §1.4 — anyone past the view-password gate can
   look at anyone's profile. Editing is gated per §1.5:
     - display fields (name, email, label) → self + admin
     - everything else (level, markets, hubspotOwnerId, isAdmin, active)
       → admin only, edited from /admin/users not here (keeps the
         metadata UI in one place).

   /me route redirects here using the active viewer's id.
*/

const { useState, useEffect, useMemo } = React;

// readWorkerUrl moved to ../lib/worker-url.ts (allowlist + self-heal).

function readViewPw() {
  try { return localStorage.getItem("forecast_view_pw") || ""; }
  catch { return ""; }
}

export function MeRedirect() {
  const { viewer, loading } = useViewer();
  if (loading) {
    // Phase 3e — Skeleton placeholder while viewer loads.
    return (
      <Page width="narrow">
        <Skeleton variant="block" height={28} width="40%" />
      </Page>
    );
  }
  // No viewer = picker hasn't been used yet. Bounce to the forecast page
  // (which surfaces the picker after the password gate).
  if (!viewer) return <Navigate to="/forecast/italy" replace />;
  // Phase 6.3 — prefer the canonical slug (HubSpot owner ID, then email
  // local-part) so the URL bar shows "/u/32360969" not "/u/u_mj9xa9b4".
  return <Navigate to={`/u/${canonicalUserSlug(viewer)}`} replace />;
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: "var(--space-3)", padding: "var(--space-2) 0", borderBottom: "1px solid var(--border)" }}>
      <span style={{ color: "var(--text-muted)", fontSize: "var(--fs-body-sm)" }}>{label}</span>
      <span>{children}</span>
    </div>
  );
}

export default function UserProfile() {
  if (!readViewPw()) {
    return <Navigate to="/forecast/italy" replace />;
  }

  const { userId } = useParams<{ userId: string }>();
  const { viewer } = useViewer();
  const lang = useLang();

  const [user, setUser] = useState<User | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [markets, setMarkets] = useState<MarketCatalogEntry[]>([]);

  // Edit state — single inline form for display fields.
  const [editing, setEditing] = useState(false);
  const [editPwInput, setEditPwInput] = useState("");
  const [editPw, setEditPw] = useState(""); // session-only
  const [form, setForm] = useState({ name: "", email: "", label: "" });
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const reload = async () => {
    if (!userId) return;
    setLoadError(null);
    try {
      const u = await getUser(userId, { workerUrl: readWorkerUrl(), viewPassword: readViewPw() });
      if (!u) { setLoadError(`User ${userId} not found.`); return; }
      setUser(u);
    } catch (e: any) {
      setLoadError(e?.message || String(e));
    }
  };
  useEffect(() => { reload(); }, [userId]);

  // Catalog used only to render marketIds as labels. Falls back silently.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cat = await listMarketCatalog({ workerUrl: readWorkerUrl(), viewPassword: readViewPw() });
        if (!cancelled) setMarkets(cat);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  const isSelf = !!viewer && !!user && viewer.id === user.id;
  const canEditDisplay = useMemo(
    () => !!viewer && !!user && canWriteUserDisplayFields(viewer, user.id),
    [viewer, user],
  );
  const canEditMetadata = useMemo(
    () => !!viewer && canWriteUserMetadata(viewer),
    [viewer],
  );

  const marketLabel = (slug: string) => markets.find(m => m.slug === slug)?.label || slug;

  const openEdit = () => {
    if (!user) return;
    setForm({ name: user.name, email: user.email, label: user.label || "" });
    setEditing(true);
    setSaveError(null);
  };
  const cancelEdit = () => { setEditing(false); setEditPwInput(""); setSaveError(null); };

  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !viewer) return;
    setBusy(true);
    setSaveError(null);
    try {
      const updated = await updateUser(viewer, user.id, {
        name: form.name.trim(),
        email: form.email.trim(),
        label: form.label.trim(),
      }, {
        workerUrl: readWorkerUrl(),
        viewPassword: readViewPw(),
        editPassword: editPw || editPwInput,
        viewerUserId: viewer.id,
      });
      // Cache the edit-password for the session if we just unlocked.
      if (!editPw && editPwInput) setEditPw(editPwInput);
      setUser(updated);
      setEditing(false);
      setEditPwInput("");
    } catch (e: any) {
      if (e?.status === 403) {
        setEditPw("");
        setSaveError("Edit password rejected. Re-enter it to continue.");
      } else {
        setSaveError(e?.message || String(e));
      }
    } finally {
      setBusy(false);
    }
  };

  // Phase A2: generic "Profile" + ViewerChip + back-link now live in the
  // global AppTopbar / AppSidebar. The user's actual name still renders as
  // an h1 below so it's discoverable when /u/<slug> loads a non-self user.
  // Phase B1: outer wrapper graduated to <Page width="narrow"> (720px token).
  return (
    <Page width="narrow">
      <h1 style={{ margin: 0, marginBottom: "var(--space-4)", fontSize: "var(--fs-h1)" }}>
        {user?.name || (loadError ? (lang === "it" ? "Profilo" : "Profile") : "…")}
      </h1>

      {loadError && (
        <div style={{ color: "var(--danger)", marginBottom: "var(--space-4)" }}>{loadError}</div>
      )}

      {user && !editing && (
        <>
          <div style={{ marginBottom: "var(--space-5)" }}>
            <FieldRow label={lang === "it" ? "Ruolo" : "Label"}>{user.label || <span style={{ color: "var(--text-muted)" }}>—</span>}</FieldRow>
            <FieldRow label="Email"><span style={{ fontFamily: "var(--font-mono)" }}>{user.email}</span></FieldRow>
            <FieldRow label={lang === "it" ? "Livello" : "Level"}>
              {user.level}
              {user.isAdmin && (
                <span style={{ marginLeft: "var(--space-2)", fontSize: "0.8em", padding: "0 var(--space-1)", borderRadius: 4, background: "var(--accent-soft)", color: "var(--accent)" }}>
                  admin
                </span>
              )}
            </FieldRow>
            <FieldRow label={lang === "it" ? "Mercati" : "Markets"}>
              {Array.isArray(user.marketIds) && user.marketIds.length > 0
                ? user.marketIds.map(marketLabel).join(", ")
                : <span style={{ color: "var(--text-muted)" }}>—</span>}
            </FieldRow>
            <FieldRow label={lang === "it" ? "ID owner HubSpot" : "HubSpot owner ID"}>
              {user.hubspotOwnerId
                ? <span style={{ fontFamily: "var(--font-mono)" }}>{user.hubspotOwnerId}</span>
                : <span style={{ color: "var(--text-muted)" }}>—</span>}
            </FieldRow>
            <FieldRow label={lang === "it" ? "Stato" : "Status"}>
              {user.active
                ? <span style={{ color: "var(--success)" }}>{lang === "it" ? "Attivo" : "Active"}</span>
                : <span style={{ color: "var(--danger)" }}>{lang === "it" ? "Disattivato" : "Deactivated"}</span>}
            </FieldRow>
            <FieldRow label={lang === "it" ? "Creato il" : "Joined"}>
              <span style={{ color: "var(--text-muted)" }}>{user.createdAt}</span>
            </FieldRow>
            <FieldRow label={lang === "it" ? "Ultimo aggiornamento" : "Last updated"}>
              <span style={{ color: "var(--text-muted)" }}>{user.updatedAt}</span>
            </FieldRow>
          </div>

          <div style={{ display: "flex", gap: "var(--space-2)" }}>
            {canEditDisplay && (
              <V2Button variant="primary" size="md" onClick={openEdit}>
                {isSelf
                  ? (lang === "it" ? "Modifica il mio profilo" : "Edit my profile")
                  : (lang === "it" ? "Modifica campi visibili" : "Edit display fields")}
              </V2Button>
            )}
            {canEditMetadata && (
              // Phase 4b audit — Link with v2-btn classes is a documented
              // exception to DR-21 (the React Router <Link> renders an
              // <a> but takes the same chrome via className composition).
              <Link to="/admin/users" className="v2-btn v2-btn--ghost v2-btn--md" style={{ textDecoration: "none" }}>
                {lang === "it" ? "Gestisci in /admin/users →" : "Manage in /admin/users →"}
              </Link>
            )}
          </div>

          {!canEditDisplay && viewer && (
            <p style={{ marginTop: "var(--space-4)", color: "var(--text-muted)", fontSize: "var(--fs-body-sm)" }}>
              {lang === "it"
                ? "Stai visualizzando il profilo di un'altra persona. I campi visibili sono modificabili solo dall'utente stesso o da un admin (§1.5)."
                : "You're viewing someone else's profile. Display fields are only editable by the user themselves or an admin (§1.5)."}
            </p>
          )}
        </>
      )}

      {/* Phase 7.2 — level-aware personal view (read-only). Sits below the
          profile fields, above the edit form. AEs see their own slice;
          MID/EXEC see links to the country views they own/cover. */}
      {user && !editing && <PersonalView user={user} />}

      {user && editing && (
        // Phase 3c — replaced 60 lines of inline-style <form>/<label>/<input>
        // markup with v2 Form + Field + Input + Button primitives. Same
        // submit flow, same fields, same error display — single shared
        // pattern across views.
        <Form onSubmit={submitEdit}>
          <V2Field label={lang === "it" ? "Nome" : "Name"} htmlFor="up-name" required>
            <Input id="up-name" value={form.name} onChange={(v: any) => setForm({ ...form, name: v })} />
          </V2Field>
          <V2Field label="Email" htmlFor="up-email" required>
            <Input id="up-email" kind="email" value={form.email} onChange={(v: any) => setForm({ ...form, email: v })} />
          </V2Field>
          <V2Field
            label={lang === "it" ? "Ruolo" : "Label"}
            htmlFor="up-label"
            required
            hint={lang === "it"
              ? "Solo titolo visibile, non influisce sui permessi."
              : "Display title only, doesn't affect permissions."}
          >
            <Input id="up-label" value={form.label} onChange={(v: any) => setForm({ ...form, label: v })} />
          </V2Field>
          {!editPw && (
            <V2Field
              label={lang === "it" ? "Password di modifica" : "Edit password"}
              htmlFor="up-editpw"
              required
              hint={lang === "it"
                ? "Il server la richiede per ogni scrittura su utenti."
                : "Server requires it for any user write."}
            >
              <Input
                id="up-editpw"
                kind="password"
                value={editPwInput}
                onChange={(v: any) => setEditPwInput(v)}
              />
            </V2Field>
          )}
          {saveError && (
            <div className="v2-field__error" role="alert">{saveError}</div>
          )}
          <Form.Actions>
            <V2Button variant="ghost" onClick={cancelEdit} disabled={busy}>
              {lang === "it" ? "Annulla" : "Cancel"}
            </V2Button>
            <V2Button type="submit" variant="primary" disabled={busy} loading={busy}>
              {busy ? (lang === "it" ? "Salvataggio…" : "Saving…") : (lang === "it" ? "Salva" : "Save")}
            </V2Button>
          </Form.Actions>
        </Form>
      )}
    </Page>
  );
}

// ─── Personal view (Phase 7.2) ─────────────────────────────────────────────
//
// Marco's directive: the platform classifies by person, not by country.
// Each user's /u/:identifier page renders the slice they're responsible
// for. The full country dashboard lives at /market/:marketId and is the
// MID's "operational" view (which they own).

const MONTH_KEYS = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"] as const;
const MONTH_LABELS_EN: Record<string, string> = {
  jan: "Jan", feb: "Feb", mar: "Mar", apr: "Apr", may: "May", jun: "Jun",
  jul: "Jul", aug: "Aug", sep: "Sep", oct: "Oct", nov: "Nov", dec: "Dec",
};

function PersonalView({ user }: { user: User }) {
  if (user.level === "AE") return <AEPersonalView user={user} />;
  if (user.level === "MID" || user.level === "EXEC") return <MarketSummaryView user={user} />;
  return null;
}

// Localised month abbreviations for the AE personal-view forecast table.
const MONTH_LABELS_IT: Record<string, string> = {
  jan: "Gen", feb: "Feb", mar: "Mar", apr: "Apr", may: "Mag", jun: "Giu",
  jul: "Lug", aug: "Ago", sep: "Set", oct: "Ott", nov: "Nov", dec: "Dic",
};

function eur(n: number): string {
  if (n == null || !isFinite(n)) return "—";
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

function AEPersonalView({ user }: { user: User }) {
  // Phase 7.3 — editable AE monthly numbers. Owner check:
  // realViewer.id === user.id AND not watching. Admins do NOT override
  // (per §1.5 #2: "AE owns own per-month numbers" — GTM Lead doesn't
  // override; Marco's call). Watch sessions are read-only by design.
  const { realViewer, isWatching } = useViewer();
  const lang = useLang();
  const monthLabels = lang === "it" ? MONTH_LABELS_IT : MONTH_LABELS_EN;
  const canEditOwnNumbers = !isWatching && !!realViewer && realViewer.id === user.id;

  const [state, setState] = useState<any>(null);
  const [actuals, setActuals] = useState<any>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);
  // drafts[mKey] = { target?, committed? } — only fields the user has touched.
  // Empty object = no pending edits. Save flushes everything in here.
  const [drafts, setDrafts] = useState<Record<string, { target?: number; committed?: number }>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const reloadDashboard = React.useCallback(async () => {
    setBusy(true);
    try {
      const [s, a] = await Promise.all([
        getSharedState({ workerUrl: readWorkerUrl(), viewPassword: readViewPw() }),
        getActuals({ workerUrl: readWorkerUrl(), viewPassword: readViewPw() }),
      ]);
      setState(s);
      setActuals(a);
      setLoadError(null);
    } catch (e: any) {
      setLoadError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const [s, a] = await Promise.all([
          getSharedState({ workerUrl: readWorkerUrl(), viewPassword: readViewPw() }),
          getActuals({ workerUrl: readWorkerUrl(), viewPassword: readViewPw() }),
        ]);
        if (!cancelled) {
          setState(s);
          setActuals(a);
        }
      } catch (e: any) {
        if (!cancelled) setLoadError(e?.message || String(e));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user.id]);

  // Match the user's hubspotOwnerId against state.aes[*].hubspotOwnerId to
  // find their AE row. Without one, we can't attribute monthly numbers.
  const aeRow = useMemo(() => {
    if (!user.hubspotOwnerId || !state?.aes) return null;
    const want = String(user.hubspotOwnerId);
    return state.aes.find((a: any) => String(a.hubspotOwnerId || "") === want) || null;
  }, [state, user.hubspotOwnerId]);

  // Filter deals to this AE's HubSpot owner ID. Sort by close date desc.
  const deals = useMemo(() => {
    if (!user.hubspotOwnerId || !actuals?.deals) return [];
    const want = String(user.hubspotOwnerId);
    return actuals.deals
      .filter((d: any) => String(d.ownerId || "") === want)
      .sort((a: any, b: any) => String(b.closeDate || "").localeCompare(String(a.closeDate || "")));
  }, [actuals, user.hubspotOwnerId]);

  const myForecastTitle = lang === "it" ? "Le mie previsioni" : "My forecast";

  if (!user.hubspotOwnerId) {
    // Phase 3e — section-level empty state primitive.
    return (
      <section style={{ marginTop: "var(--space-6)" }}>
        <h2 style={{ margin: 0, marginBottom: "var(--space-3)", fontSize: "var(--fs-h3)" }}>{myForecastTitle}</h2>
        <EmptyState
          title={lang === "it" ? "Nessun owner ID HubSpot impostato" : "No HubSpot owner ID set"}
          message={lang === "it"
            ? "Chiedi a un admin di collegare il tuo utente a un owner HubSpot per attribuire deal e numeri."
            : "Ask an admin to map your user to a HubSpot owner so your deals + numbers can be attributed."}
        />
      </section>
    );
  }

  if (busy) {
    // Phase 3e — Skeleton placeholder while AE state + actuals load.
    return (
      <section style={{ marginTop: "var(--space-6)" }}>
        <h2 style={{ margin: 0, marginBottom: "var(--space-3)", fontSize: "var(--fs-h3)" }}>{myForecastTitle}</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          <Skeleton variant="block" height={32} />
          <Skeleton variant="block" height={32} />
          <Skeleton variant="block" height={32} />
          <Skeleton variant="block" height={32} />
        </div>
      </section>
    );
  }

  if (loadError) {
    // Phase 3e — ErrorState primitive replaces the inline danger paragraph.
    return (
      <section style={{ marginTop: "var(--space-6)" }}>
        <h2 style={{ margin: 0, marginBottom: "var(--space-3)", fontSize: "var(--fs-h3)" }}>{myForecastTitle}</h2>
        <ErrorState
          lang={lang}
          title={lang === "it" ? "Impossibile caricare le previsioni" : "Couldn't load forecast"}
          message={loadError}
          onRetry={reloadDashboard}
          retryLabel={lang === "it" ? "Riprova" : "Retry"}
        />
      </section>
    );
  }

  const aeId = aeRow?.id;
  // Overlay any unsaved drafts on top of the KV state so the table reflects
  // what the user has typed, not what's currently persisted.
  const rows = MONTH_KEYS.map(mKey => {
    const cell = aeId ? state?.months?.[mKey]?.aes?.[aeId] : null;
    const draft = drafts[mKey] || {};
    const target    = Number(draft.target    ?? cell?.target    ?? 0);
    const real      = Number(cell?.real ?? 0);
    const committed = Number(draft.committed ?? cell?.committed ?? 0);
    return { mKey, target, real, committed };
  });

  const totals = rows.reduce((acc, r) => ({
    target:    acc.target + r.target,
    real:      acc.real + r.real,
    committed: acc.committed + r.committed,
  }), { target: 0, real: 0, committed: 0 });

  const hasPendingEdits = Object.keys(drafts).length > 0;

  const setDraft = (mKey: string, field: "target" | "committed", value: number) => {
    setDrafts(prev => ({ ...prev, [mKey]: { ...(prev[mKey] || {}), [field]: value } }));
  };

  const discardDrafts = () => { setDrafts({}); setSaveError(null); };

  const saveDrafts = async () => {
    if (!hasPendingEdits || !aeId || !realViewer) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Build minimal scope:ae payload. The worker merges this against the
      // canonical state and filters to AE rows the operator owns (which,
      // for self-edit, is exactly aeId). Real values stay HubSpot-driven —
      // we only send target + committed.
      const months: any = {};
      for (const [mKey, d] of Object.entries(drafts)) {
        const existing = state?.months?.[mKey]?.aes?.[aeId] || {};
        months[mKey] = {
          aes: {
            [aeId]: {
              ...existing,
              ...(d.target    != null ? { target:    Number(d.target)    } : {}),
              ...(d.committed != null ? { committed: Number(d.committed) } : {}),
            },
          },
        };
      }
      const resp = await fetch(`${readWorkerUrl()}/api/state`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-View-Password": readViewPw(),
          "X-Edit-Scope": "ae",
          // Worker resolves owner from this. Send realViewer.id so audit
          // attributes correctly even if the URL is e.g. ?as=someone.
          "X-User-Id": realViewer.id,
        },
        body: JSON.stringify({ months }),
      });
      if (!resp.ok) {
        let body: any = null; try { body = await resp.json(); } catch {}
        throw new Error(body?.error || `Save failed (${resp.status})`);
      }
      // Pull fresh state so the table shows the canonical KV values, not
      // an optimistic guess. Clears drafts on success.
      await reloadDashboard();
      setDrafts({});
    } catch (e: any) {
      setSaveError(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <section style={{ marginTop: "var(--space-6)" }}>
        <h2 style={{ margin: 0, marginBottom: "var(--space-3)", fontSize: "var(--fs-h3)" }}>{myForecastTitle}</h2>
        {!aeRow && (
          <p style={{ color: "var(--text-muted)", fontSize: "var(--fs-body-sm)" }}>
            {lang === "it"
              ? "Il tuo owner ID HubSpot non è ancora nella roster AE — chiedi a un admin di aggiungerlo."
              : "Your HubSpot owner ID isn't in the AE roster yet — ask an admin to add it."}
          </p>
        )}
        {aeRow && (
          // Phase 4b audit — DR-23: route the my-forecast table through
          // the v2 DataTable primitive. Inline numeric inputs in the
          // editable cells remain raw <input type="number"> — they are
          // tiny per-cell controls (90px wide) where a full V2Field +
          // Input chain would add label/error chrome that doesn't fit
          // inside a table cell. The cell controls inherit v2-input
          // styling via the targeted CSS rule in components.css §15a
          // (Phase 4b adds it below if needed).
          <DataTable density="compact" stickyHeader={false}>
            <thead>
              <tr>
                <th>{lang === "it" ? "Mese" : "Month"}</th>
                <th style={{ textAlign: "right" }}>{lang === "it" ? "Target" : "Target"}</th>
                <th style={{ textAlign: "right" }}>{lang === "it" ? "Impegnato" : "Committed"}</th>
                <th style={{ textAlign: "right" }}>{lang === "it" ? "Reale" : "Real"}</th>
                <th style={{ textAlign: "right" }}>{lang === "it" ? "vs Target" : "vs Target"}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const pct = r.target > 0 ? Math.round((r.real / r.target) * 100) : null;
                const hasDraft = drafts[r.mKey];
                return (
                  <tr key={r.mKey} style={{ background: hasDraft ? "var(--accent-soft)" : undefined }}>
                    <td>{monthLabels[r.mKey]}</td>
                    <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>
                      {canEditOwnNumbers ? (
                        <input
                          type="number"
                          min={0}
                          step={100}
                          value={r.target}
                          onChange={(e) => setDraft(r.mKey, "target", Number(e.target.value))}
                          disabled={saving}
                          aria-label={`${lang === "it" ? "Target per" : "Target for"} ${monthLabels[r.mKey]}`}
                          className="v2-input v2-input--sm v2-input--cell"
                        />
                      ) : eur(r.target)}
                    </td>
                    <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>
                      {canEditOwnNumbers ? (
                        <input
                          type="number"
                          min={0}
                          step={100}
                          value={r.committed}
                          onChange={(e) => setDraft(r.mKey, "committed", Number(e.target.value))}
                          disabled={saving}
                          aria-label={`${lang === "it" ? "Impegnato per" : "Committed for"} ${monthLabels[r.mKey]}`}
                          className="v2-input v2-input--sm v2-input--cell"
                        />
                      ) : eur(r.committed)}
                    </td>
                    {/* Phase 4b audit (DR-14) — native title= on <td>
                        migrated to <Tooltip> wrapping the cell content.
                        The tooltip can't wrap the <td> itself (Tooltip
                        renders a <span> which would break table layout)
                        so the hint is on the inner value. */}
                    <td style={{ textAlign: "right", fontFamily: "var(--font-mono)", color: "var(--text-muted)" }}>
                      <Tooltip tip={lang === "it"
                        ? "Il reale viene da HubSpot — non modificabile qui"
                        : "Real comes from HubSpot — not editable here"}>
                        <span>{eur(r.real)}</span>
                      </Tooltip>
                    </td>
                    {/* Phase 3d — icon + colored % so the signal isn't
                        color-only. ↑ = ≥100 (good), ↓ = <80 (danger),
                        − = 80–99 (neutral). */}
                    <td style={{ textAlign: "right", color: pct == null ? "var(--text-muted)" : pct >= 100 ? "var(--success)" : pct >= 80 ? "var(--text-primary)" : "var(--danger)" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: "var(--space-0-5)" }}>
                        {pct == null ? null
                          : pct >= 100 ? <ArrowUp   size={12} />
                          : pct >= 80  ? <Minus     size={12} />
                                       : <ArrowDown size={12} />}
                        {pct == null ? "—" : `${pct}%`}
                      </span>
                    </td>
                  </tr>
                );
              })}
              <tr style={{ fontWeight: 600 }}>
                <td>FY</td>
                <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>{eur(totals.target)}</td>
                <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>{eur(totals.committed)}</td>
                <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>{eur(totals.real)}</td>
                <td style={{ textAlign: "right" }}>
                  {totals.target > 0 ? `${Math.round((totals.real / totals.target) * 100)}%` : "—"}
                </td>
              </tr>
            </tbody>
          </DataTable>
        )}

        {/* Phase 7.3 — save controls. Only rendered when the real session
            user is the AE being viewed AND there are pending changes.
            Read-only viewers (someone else's page, or watching as them)
            see neither inputs nor save controls. */}
        {aeRow && canEditOwnNumbers && (
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginTop: "var(--space-3)" }}>
            {hasPendingEdits ? (
              <>
                <V2Button variant="primary" size="md" onClick={saveDrafts} disabled={saving} loading={saving}>
                  {saving
                    ? (lang === "it" ? "Salvataggio…" : "Saving…")
                    : (lang === "it"
                        ? `Salva modifiche (${Object.keys(drafts).length} ${Object.keys(drafts).length === 1 ? "mese" : "mesi"})`
                        : `Save changes (${Object.keys(drafts).length} month${Object.keys(drafts).length === 1 ? "" : "s"})`)}
                </V2Button>
                <V2Button variant="ghost" size="md" onClick={discardDrafts} disabled={saving}>
                  {lang === "it" ? "Scarta" : "Discard"}
                </V2Button>
              </>
            ) : (
              <span style={{ color: "var(--text-muted)", fontSize: "var(--fs-body-sm)" }}>
                {lang === "it"
                  ? "Digita nuovi valori in Target o Impegnato per modificare. Il reale viene da HubSpot."
                  : "Type new values into Target or Committed to edit. Real comes from HubSpot."}
              </span>
            )}
            {saveError && (
              <span style={{ color: "var(--danger)", fontSize: "var(--fs-body-sm)" }}>{saveError}</span>
            )}
          </div>
        )}
        {aeRow && !canEditOwnNumbers && (
          <p style={{ marginTop: "var(--space-3)", color: "var(--text-muted)", fontSize: "var(--fs-body-sm)" }}>
            {isWatching
              ? (lang === "it"
                  ? "Sola lettura — stai osservando questo utente. Esci dalla modalità watch dal chip per modificare le tue previsioni."
                  : "Read-only — you're watching this user. Exit watch on your chip to edit your own forecast.")
              : (lang === "it"
                  ? "Sola lettura — solo l'AE stesso può modificare i propri numeri mensili (§1.5)."
                  : "Read-only — only the AE themselves can edit their per-month numbers (§1.5).")}
          </p>
        )}
      </section>

      <section style={{ marginTop: "var(--space-6)" }}>
        <h2 style={{ margin: 0, marginBottom: "var(--space-3)", fontSize: "var(--fs-h3)" }}>
          {lang === "it" ? "I miei deal" : "My deals"} <span style={{ color: "var(--text-muted)", fontWeight: 400, fontSize: "var(--fs-body-sm)" }}>({deals.length})</span>
        </h2>
        {deals.length === 0 ? (
          // Phase 3e — empty state primitive.
          <EmptyState
            size="compact"
            title={lang === "it" ? "Nessun deal" : "No deals"}
            message={lang === "it"
              ? "Nessun deal di questo owner HubSpot nell'anno fiscale corrente."
              : "No deals owned by this HubSpot user in the current fiscal year."}
          />
        ) : (
          // Phase 4b audit — DR-23: my-deals table through DataTable.
          <DataTable density="compact" stickyHeader={false}>
            <thead>
              <tr>
                <th>{lang === "it" ? "Nome" : "Name"}</th>
                <th>{lang === "it" ? "Azienda" : "Company"}</th>
                <th>{lang === "it" ? "Chiusura" : "Close"}</th>
                <th style={{ textAlign: "right" }}>{lang === "it" ? "Importo" : "Amount"}</th>
                <th>{lang === "it" ? "Stage" : "Stage"}</th>
              </tr>
            </thead>
            <tbody>
              {deals.slice(0, 50).map((d: any) => (
                <tr key={d.id}>
                  <td>{d.name || (lang === "it" ? "(senza nome)" : "(no name)")}</td>
                  <td style={{ color: "var(--text-muted)" }}>{d.company || "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{d.closeDate ? String(d.closeDate).slice(0, 10) : "—"}</td>
                  <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>{eur(d.amount)}</td>
                  <td style={{ color: "var(--text-muted)" }}>{d.cell || d.stage}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
        {deals.length > 50 && (
          <p style={{ marginTop: "var(--space-2)", color: "var(--text-muted)", fontSize: "var(--fs-body-sm)" }}>
            {lang === "it"
              ? `Mostro i 50 più recenti di ${deals.length}. La dashboard del paese su /market/italy mostra la tabella completa.`
              : `Showing the 50 most recent of ${deals.length}. The country dashboard at /market/italy has the full table.`}
          </p>
        )}
      </section>
    </>
  );
}

// Phase 7.4 — MarketSummaryView replaces the bare MarketLinksView.
// One card per market in user.marketIds. Italy renders a real summary
// (FY totals: target / real / attainment, # of deals); other markets
// render a "not yet onboarded" placeholder because the data model is
// still single-market under the hood (Italy hardcoded in worker queries
// + fiscal-year constants). Future Phase 7.x sub-steps thread marketId
// through the worker so other countries get real numbers too.
//
// Phase 7.5a — supported-market check moved to lib/markets-config.ts
// so adding a new country only requires touching that single file.

function MarketSummaryView({ user }: { user: User }) {
  const lang = useLang();
  const markets = Array.isArray(user.marketIds) ? user.marketIds : [];

  const [state, setState] = useState<any>(null);
  const [actuals, setActuals] = useState<any>(null);
  const [catalog, setCatalog] = useState<MarketCatalogEntry[]>([]);
  const [busy, setBusy] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const [s, a, c] = await Promise.all([
          getSharedState({ workerUrl: readWorkerUrl(), viewPassword: readViewPw() }),
          getActuals({ workerUrl: readWorkerUrl(), viewPassword: readViewPw() }),
          listMarketCatalog({ workerUrl: readWorkerUrl(), viewPassword: readViewPw() })
            .catch(() => [] as MarketCatalogEntry[]),
        ]);
        if (!cancelled) {
          setState(s);
          setActuals(a);
          setCatalog(c);
        }
      } catch (e: any) {
        if (!cancelled) setLoadError(e?.message || String(e));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user.id]);

  if (markets.length === 0) {
    // Phase 3e — empty state primitive.
    return (
      <section style={{ marginTop: "var(--space-6)" }}>
        <h2 style={{ margin: 0, marginBottom: "var(--space-3)", fontSize: "var(--fs-h3)" }}>{lang === "it" ? "Mercati" : "Markets"}</h2>
        <EmptyState
          title={lang === "it" ? "Nessun mercato assegnato" : "No markets assigned"}
          message={lang === "it"
            ? "Chiedi a un admin di assegnarti uno o più mercati."
            : "Ask an admin to map you to one or more markets."}
        />
      </section>
    );
  }

  const labelOf = (slug: string) => catalog.find(m => m.slug === slug)?.label || slug;

  return (
    <section style={{ marginTop: "var(--space-6)" }}>
      <h2 style={{ margin: 0, marginBottom: "var(--space-3)", fontSize: "var(--fs-h3)" }}>
        {user.level === "MID"
          ? (lang === "it"
              ? `Il mio mercato${markets.length > 1 ? "i" : ""}`
              : `My market${markets.length > 1 ? "s" : ""}`)
          : (lang === "it" ? "Mercati coperti" : "Markets covered")}
      </h2>
      <p style={{ marginTop: 0, marginBottom: "var(--space-4)", color: "var(--text-muted)", fontSize: "var(--fs-body-sm)" }}>
        {user.level === "MID"
          ? (lang === "it"
              ? "Possiedi il forecast GTM per i mercati elencati. Clicca per aprire la dashboard operativa."
              : "You own the GTM forecast for the markets below. Click to open the operational dashboard.")
          : (lang === "it"
              ? "Vista executive — lettura su ogni paese."
              : "Executive view — read across each country.")}
      </p>

      {loadError && (
        <div style={{ color: "var(--danger)", fontSize: "var(--fs-body-sm)", marginBottom: "var(--space-3)" }}>{loadError}</div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "var(--space-3)" }}>
        {markets.map(slug => (
          <MarketSummaryCard
            key={slug}
            slug={slug}
            label={labelOf(slug)}
            state={state}
            actuals={actuals}
            busy={busy}
          />
        ))}
      </div>
    </section>
  );
}

function MarketSummaryCard({
  slug, label, state, actuals, busy,
}: {
  slug: string;
  label: string;
  state: any;
  actuals: any;
  busy: boolean;
}) {
  const lang = useLang();
  const supported = isMarketSupported(slug);
  // FY totals — only meaningful for italy today. Aggregate gtmTarget
  // across the 12 month buckets in state.months, and gtmActual + the
  // "other" cross-check bucket across actuals.months. Closely mirrors
  // the legacy KpiStrip math but condensed.
  const summary = useMemo(() => {
    if (!supported || !state || !actuals) return null;
    let target = 0;
    let real = 0;
    let dealCount = 0;
    for (const mKey of MONTH_KEYS) {
      target += Number(state?.months?.[mKey]?.gtmTarget || 0);
      real   += Number(actuals?.months?.[mKey]?.gtmActual || 0);
      // include "other" reads-as-Italy bucket if present
      real   += Number(actuals?.months?.[mKey]?.otherActual || 0);
    }
    if (Array.isArray(actuals?.deals)) dealCount = actuals.deals.length;
    return { target, real, dealCount };
  }, [supported, state, actuals]);

  const pct = summary && summary.target > 0
    ? Math.round((summary.real / summary.target) * 100)
    : null;

  return (
    <Link
      to={`/market/${slug}`}
      style={{
        display: "block",
        padding: "var(--space-4)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-md)",
        background: "var(--surface)",
        textDecoration: "none",
        color: "var(--text-primary)",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "var(--space-3)" }}>
        <span style={{ fontSize: "var(--fs-body-lg)", fontWeight: 600 }}>{label}</span>
        <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-muted)", fontSize: "var(--fs-body-sm)" }}>
          /market/{slug}
        </span>
      </div>
      {!supported && (
        <div style={{ color: "var(--text-muted)", fontSize: "var(--fs-body-sm)" }}>
          {lang === "it"
            ? "Non ancora attivato. La dashboard placeholder apre ma non avrà dati finché questo mercato non è collegato."
            : "Not yet onboarded. The dashboard placeholder opens but won't have data until this market is wired up."}
        </div>
      )}
      {supported && busy && (
        // Phase 3e — Skeleton placeholder for the summary card.
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" />
        </div>
      )}
      {supported && !busy && summary && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-2)", marginBottom: "var(--space-3)" }}>
            <SummaryStat label={lang === "it" ? "Target FY" : "FY target"} value={eur(summary.target)} />
            <SummaryStat label={lang === "it" ? "Reale FY" : "FY real"} value={eur(summary.real)} />
          </div>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", borderTop: "1px solid var(--border)", paddingTop: "var(--space-2)" }}>
            <span style={{ color: "var(--text-muted)", fontSize: "var(--fs-body-sm)" }}>{lang === "it" ? "Raggiungimento" : "Attainment"}</span>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 2,
              fontFamily: "var(--font-mono)", fontWeight: 600,
              color: pct == null
                ? "var(--text-muted)"
                : pct >= 100
                  ? "var(--success)"
                  : pct >= 80
                    ? "var(--text-primary)"
                    : "var(--danger)",
            }}>
              {/* Phase 3d — direction icon pairs with attainment color. */}
              {pct == null ? null
                : pct >= 100 ? <ArrowUp   size={12} />
                : pct >= 80  ? <Minus     size={12} />
                             : <ArrowDown size={12} />}
              {pct == null ? "—" : `${pct}%`}
            </span>
          </div>
          <div style={{ marginTop: "var(--space-2)", color: "var(--text-muted)", fontSize: "var(--fs-body-sm)" }}>
            {lang === "it"
              ? `${summary.dealCount} deal in pipeline · Apri dashboard →`
              : `${summary.dealCount} deals in pipeline · Open dashboard →`}
          </div>
        </>
      )}
    </Link>
  );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ color: "var(--text-muted)", fontSize: "var(--fs-body-sm)" }}>{label}</div>
      <div style={{ fontFamily: "var(--font-mono)", fontWeight: 600 }}>{value}</div>
    </div>
  );
}
